import axios, { AxiosResponse } from 'axios'
import { MIXPANEL_TOKEN, isLocalEnvironment } from 'config'
import { MIXPANEL_EVENTS } from 'constants/enums/MixpanelEvents'
import Mixpanel from 'mixpanel-browser'

import { CreateGame, Game, GetGames } from '../models/gameModels'
import { getFacilitationTechnique } from '../services/analytics.service'
import { getSignedRequest } from '../utils/authUtils'

export const postGames = async (
	data: CreateGame
): Promise<AxiosResponse<Game>> => {
	const signed = await getSignedRequest('POST', 'games', JSON.stringify(data))

	if (signed) {
		const response = await axios({
			...signed,
			url: (process.env.REACT_APP_AWS_API_URL as string) + 'games',
			data,
			method: 'POST',
		})

		// Track successful game creation (silent failure - don't block user action)
		if (response.status === 200 && !isLocalEnvironment && MIXPANEL_TOKEN) {
			try {
				Mixpanel.track(MIXPANEL_EVENTS.CREATE_GAME, {
					gameTypeId: data.gameTypeId,
					facilitationTechnique: getFacilitationTechnique(
						data.gameTypeId
					),
				})
			} catch {
				// Analytics failure should not block game creation
			}
		}

		return response
	} else {
		throw new Error('Unable to get signed request')
	}
}

export const getGames = async (
	gameSearch: GetGames
): Promise<AxiosResponse<Game[]>> => {
	let params = ''
	if (gameSearch.gameTitle != null) {
		params += `gameTitle=${gameSearch.gameTitle}`
	}
	if (gameSearch.gameTypeId != null) {
		if (params !== '') {
			params += '&'
		}
		params += `gameTypeId=${gameSearch.gameTypeId}`
	}

	const signed = await getSignedRequest('GET', `games?${params}`)

	if (signed) {
		return await axios({
			...signed,
			url:
				(process.env.REACT_APP_AWS_API_URL as string) +
				`games?${params}`,
			method: 'GET',
		})
	} else {
		throw new Error('Unable to get signed request')
	}
}

export const getGame = async (gameId: string): Promise<AxiosResponse<Game>> => {
	const signed = await getSignedRequest('GET', `games/${gameId}`)
	if (signed) {
		return await axios({
			...signed,
			url:
				(process.env.REACT_APP_AWS_API_URL as string) +
				`games/${gameId}`,
			method: 'GET',
		})
	} else {
		throw new Error('Unable to get signed request')
	}
}

export const editGame = async (
	// gameId: string,
	data: Game
): Promise<AxiosResponse<Game>> => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { isAuthor, isParticipant, ...gamePutRequest } = data
	const signed = await getSignedRequest(
		'PUT',
		`games/${gamePutRequest.gameId}`,
		JSON.stringify(gamePutRequest)
	)

	if (signed) {
		return await axios({
			...signed,
			url:
				(process.env.REACT_APP_AWS_API_URL as string) +
				`games/${data.gameId}`,
			data: gamePutRequest,
			method: 'PUT',
		})
	} else {
		throw new Error('Unable to get signed request')
	}
}

export const deleteGame = async (
	gameId: string
): Promise<AxiosResponse<Game>> => {
	const signed = await getSignedRequest('DELETE', `games/${gameId}`)

	if (signed) {
		return await axios({
			...signed,
			url:
				(process.env.REACT_APP_AWS_API_URL as string) +
				`games/${gameId}`,
			method: 'DELETE',
		})
	} else {
		throw new Error('Unable to get signed request')
	}
}
